import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import Navbar from "./Navbar";
import DashboardHeader from "./DashboardHeader";
import api from "../api";
import Footer from "./Footer";
import { useTheme } from "../App"; // 🔥 Import the global theme hook

/* ================= HELPERS ================= */
const getCurrentMonthName = () => {
  const date = new Date();
  return date.toLocaleString("default", { month: "long", year: "numeric" });
};

// Generate a list of months for the dropdown (3 months past, 9 months future)
const generateMonthOptions = () => {
  const months = [];
  const date = new Date();
  date.setMonth(date.getMonth() - 3); 
  for (let i = 0; i < 12; i++) {
    months.push(date.toLocaleString('default', { month: 'long', year: 'numeric' }));
    date.setMonth(date.getMonth() + 1);
  }
  return months;
};

const formatScore = (value) => Number(value || 0).toFixed(1);

const clampPercent = (value, max) => {
  if (!max) return 0;
  const percent = (Number(value || 0) / max) * 100;
  return Math.max(0, Math.min(percent, 100));
};

/* ================= STYLED COMPONENTS ================= */
const Container = styled.div`
  --sidebar-expanded: 260px;
  --sidebar-collapsed: 90px;
  
  /* 🔥 THEME VARIABLES 🔥 */
  --page-bg: ${({ $isDarkMode }) => $isDarkMode ? 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)' : 'linear-gradient(180deg, #f8fafc 0%, #eef4ff 100%)'};
  --card-bg: ${({ $isDarkMode }) => $isDarkMode ? '#1e293b' : 'rgba(255, 255, 255, 0.92)'};
  --text-main: ${({ $isDarkMode }) => $isDarkMode ? '#f8fafc' : '#0f172a'};
  --text-soft: ${({ $isDarkMode }) => $isDarkMode ? '#94a3b8' : '#64748b'};
  --border: ${({ $isDarkMode }) => $isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(148, 163, 184, 0.18)'};
  --input-bg: ${({ $isDarkMode }) => $isDarkMode ? '#0f172a' : '#f8fbff'};
  --input-disabled: ${({ $isDarkMode }) => $isDarkMode ? '#162032' : '#f1f5f9'};
  --input-border: ${({ $isDarkMode }) => $isDarkMode ? '#334155' : '#dbe3ef'};
  
  --shadow-md: 0 10px 30px rgba(0, 0, 0, 0.15);

  min-height: 100vh;
  background: var(--page-bg);
  color: var(--text-main);
  margin-left: ${({ collapsed }) => collapsed ? "var(--sidebar-collapsed)" : "var(--sidebar-expanded)"};
  transition: margin-left 0.3s ease, background 0.3s ease, color 0.3s ease;

  @media (max-width: 768px) { margin-left: 0; }
`;

const Main = styled.main`
  width: min(1200px, calc(100% - 32px)); margin: 0 auto; padding: 12px 0 40px;
      @media (max-width: 1024px) {
    width: min(100%, calc(100% - 24px));
    padding: 12px 0 32px;
  }

  @media (max-width: 576px) {
    width: min(100%, calc(100% - 16px));
    padding: 12px 0 24px;
  }
`;

const Hero = styled.section`
  display: grid; grid-template-columns: 1.35fr 0.95fr; gap: 24px; margin-bottom: 24px;
  @media (max-width: 992px) { grid-template-columns: 1fr; }
`;

const HeroPanel = styled.div`
  background: linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #2563eb 100%); color: white;
  padding: 18px; border-radius: 28px; display: flex; flex-direction: column; justify-content: center;
`;

const HeroTitle = styled.h1` margin: 0 0 10px; font-size: clamp(1.6rem, 1.2rem + 1.5vw, 2.5rem); font-weight: 800; `;
const HeroText = styled.p` margin: 0; color: rgba(255, 255, 255, 0.9); line-height: 1.65; font-size: 1.05rem; `;

const SnapshotCard = styled.div`
  background: var(--card-bg); border: 1px solid var(--border); border-radius: 28px; padding: 24px; box-shadow: var(--shadow-md);
  transition: background 0.3s ease, border 0.3s ease;
`;

const SnapshotLabel = styled.div` font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.08em; color: var(--text-soft); font-weight: 700; `;
const SnapshotValue = styled.div` font-size: clamp(1.5rem, 1.2rem + 1.5vw, 3rem); font-weight: 800; color: var(--text-main); `;

const GradeBadge = styled.div`
  display: inline-flex; align-items: center; padding: 10px 20px; border-radius: 999px;
  background: rgba(37, 99, 235, 0.12); color: ${({ $isDarkMode }) => $isDarkMode ? '#60a5fa' : '#1d4ed8'}; font-weight: 800;
`;

const Grid = styled.section`
  display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 20px; margin: 24px 0 28px;
  @media (max-width: 1100px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const Card = styled.div`
  background: var(--card-bg); border: 1px solid var(--border); border-radius: 24px; padding: 24px; box-shadow: var(--shadow-md);
  transition: background 0.3s ease, border 0.3s ease;
`;

const Title2 = styled.h3` margin-bottom: 5px; font-size: 0.95rem; color: var(--text-soft); font-weight: 700; `;
const Title = styled.h3` margin: 0; font-size: 0.95rem; color: var(--text-soft); font-weight: 700; line-height: 1.4; min-height: 2.8rem; `;
const Title1 = styled.h3` margin: 10px 0 10px 20px; font-size: 0.95rem; color: var(--text-soft); font-weight: 700; `;
const Score = styled.div` font-size: 2.2rem; font-weight: 800; margin-top: 16px; color: var(--text-main); `;

const ProgressTrack = styled.div`
  height: 10px; border-radius: 999px; overflow: hidden; margin-top: 16px;
  background: ${({ variant, $isDarkMode }) =>
    variant === "green" ? ($isDarkMode ? 'rgba(34, 197, 94, 0.15)' : "#dcfce7")
    : variant === "purple" ? ($isDarkMode ? 'rgba(168, 85, 247, 0.15)' : "#f3e8ff")
    : variant === "orange" ? ($isDarkMode ? 'rgba(249, 115, 22, 0.15)' : "#ffedd5")
    : variant === "red" ? ($isDarkMode ? 'rgba(239, 68, 68, 0.15)' : "#fee2e2")
    : ($isDarkMode ? '#334155' : "#e2e8f0")};
`;

const ProgressFill = styled.div`
  height: 100%; width: ${({ width }) => width}%; border-radius: 999px; transition: width 0.4s ease;
  background: ${({ variant }) =>
    variant === "green" ? "linear-gradient(90deg, #16a34a, #22c55e)"
    : variant === "purple" ? "linear-gradient(90deg, #7c3aed, #a855f7)"
    : variant === "orange" ? "linear-gradient(90deg, #ea580c, #fb923c)"
    : variant === "red" ? "linear-gradient(90deg, #dc2626, #ef4444)"
    : "linear-gradient(90deg, #2563eb, #60a5fa)"};
`;
/* ================= UPDATED EMPTY STATE STYLES ================= */
const SectionTitleWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh; /* Gives it vertical height so it centers on the page */
  width: 100%;
  text-align: center;
`;

const SectionTitle = styled.h2`
  font-size: clamp(1.5rem, 1.2rem + 1vw, 2rem);
  font-weight: 800;
  color: var(--text-soft); /* Using text-soft makes empty states look better */
  margin: 0;
  padding: 20px;
  background: var(--card-bg);
  border: 1px dashed var(--border);
  border-radius: 20px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.03);
`;

const TitleRow = styled.div` display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 24px; `;


const GlobalMonthSelect = styled.select`
  padding: 8px 16px; border-radius: 12px; border: 2px solid ${({ $isDarkMode }) => $isDarkMode ? '#3b82f6' : '#bfdbfe'}; 
  font-size: 1.2rem; font-weight: 800; color: #3b82f6; 
  background: ${({ $isDarkMode }) => $isDarkMode ? 'rgba(59, 130, 246, 0.1)' : '#eff6ff'}; 
  outline: none; cursor: pointer; transition: all 0.2s;
  &:focus { border-color: #2563eb; box-shadow: 0 0 0 4px rgba(59,130,246,0.1); }
`;

const FormCard = styled.section`
  background: var(--card-bg); border: 1px solid var(--border); border-radius: 28px; padding: 28px; box-shadow: var(--shadow-md); margin-bottom: 24px;
  transition: background 0.3s ease, border 0.3s ease;
`;

const FormTitle = styled.h3` margin: 0 0 12px; font-size: 1.1rem; font-weight: 800; text-transform: uppercase; color: var(--text-main); `;

const TextArea = styled.textarea`
  width: 100%; min-height: ${({ tall }) => (tall ? "140px" : "120px")}; border-radius: 18px; border: 1px solid var(--input-border);
  background: ${({ disabled }) => (disabled ? "var(--input-disabled)" : "var(--input-bg)")};
  color: var(--text-main); padding: 16px; font-size: 0.98rem; resize: vertical;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "text")}; transition: background 0.3s ease, border 0.3s ease, color 0.3s ease;
  &:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12); }
`;

const FooterActions = styled.div` margin-top: 24px; display: flex; width: 100%; `;

const Button = styled.button`
  border-radius: 16px; padding: 18px 24px; width: 100%; border: none;
  background: ${({ disabled }) => (disabled ? "var(--border)" : "#3b82f6")};
  color: ${({ disabled }) => (disabled ? "var(--text-soft)" : "white")};
  font-size: 1.1rem; font-weight: 800; cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition: background 0.2s ease;
  &:hover:not(:disabled) { background: #2563eb; }
`;

const ButtonSecondary = styled(Button)`
  background: transparent; color: #3b82f6; border: 2px solid #3b82f6;
  &:hover:not(:disabled) { background: rgba(59, 130, 246, 0.1); }
`;

const Message = styled.div`
  padding: 14px 20px; border-radius: 12px; font-weight: 700; margin-bottom: 16px; text-align: center;
  ${({ type, $isDarkMode }) => {
    if (type === "success") return $isDarkMode ? `background: rgba(16, 185, 129, 0.1); color: #34d399; border: 1px solid rgba(52, 211, 153, 0.3);` : `background: #ecfdf5; color: #10b981; border: 1px solid #86efac;`;
    return $isDarkMode ? `background: rgba(239, 68, 68, 0.1); color: #f87171; border: 1px solid rgba(248, 113, 113, 0.3);` : `background: #fee2e2; color: #dc2626; border: 1px solid #f87171;`;
  }}
`;

const CenterState = styled.div` min-height: 55vh; display: grid; place-items: center; `;
const StateCard = styled.div` width: min(460px, 100%); background: var(--card-bg); border: 1px solid var(--border); border-radius: 24px; padding: 28px; text-align: center; box-shadow: var(--shadow-md); `;
const Spinner = styled.div` width: 46px; height: 46px; margin: 0 auto 18px; border-radius: 50%; border: 4px solid var(--border); border-top-color: #7c3aed; animation: spin 0.8s linear infinite; @keyframes spin { to { transform: rotate(360deg); } } `;
const StateTitle = styled.h3` margin: 0; font-size: 1.1rem; color: var(--text-main); `;
const StateText = styled.p` margin: 10px 0 0; color: var(--text-soft); line-height: 1.6; `;

/* ================= MAIN COMPONENT ================= */
const Summary = ({ user, collapsed, setCollapsed }) => {
  const [data, setData] = useState(null);
  const [citation, setCitation] = useState("");


  // 🔥 GLOBAL THEME HOOK 🔥
  const { isDarkMode } = useTheme();

  // Generate options for month selector
  const availableMonths = useMemo(() => generateMonthOptions(), []);

  // 🔥 GLOBAL MONTH LOGIC 🔥
  const [selectedMonth, setSelectedMonth] = useState(
    localStorage.getItem("globalSelectedMonth") || getCurrentMonthName()
  );

  // Sync to local storage
  useEffect(() => {
    localStorage.setItem("globalSelectedMonth", selectedMonth);
  }, [selectedMonth]);


  // if (loading) {
  //   return (
  //     <Container collapsed={collapsed} $isDarkMode={isDarkMode}>
  //       <Navbar user={user} collapsed={collapsed} setCollapsed={setCollapsed} />
  //       <DashboardHeader title="Summary of Performance" />
  //       <CenterState>
  //         <StateCard>
  //           <Spinner />
  //           <StateTitle>Loading Summary of Performance</StateTitle>
  //           <StateText>Please wait while your records for {selectedMonth} are being prepared.</StateText>
  //         </StateCard>
  //       </CenterState>
  //     </Container>
  //   );
  // }
  
  // if (!data) return null;

  const charCount = citation.length;
  const overLimit = charCount > 500;

  return (
    // 🔥 PASS THE THEME STATE TO THE CONTAINER 🔥
    <Container collapsed={collapsed} $isDarkMode={isDarkMode}>
      <Navbar user={user} collapsed={collapsed} setCollapsed={setCollapsed} />
      <DashboardHeader user={user} collapsed={collapsed} title="Work Ethic & Leadership Attributes" />


      <Main>
        {/* <Hero> */}
          <HeroPanel>
            <HeroTitle>Training</HeroTitle>
          </HeroPanel>
            {/* </Hero> */}
                    <SectionTitleWrap>
              <SectionTitle>No content yet</SectionTitle>
        </SectionTitleWrap>
        

      </Main>
      <Footer />
    </Container>
  );
};

export default Summary;